import {
  ManualPaymentAdjustmentType,
  OrderManualPaymentAdjustmentDTO,
  OrderManualPaymentDetailsDTO,
  OrderManualPaymentSource,
} from '@reposit/api-client';
import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { penceToPounds } from '../../utils/common.utils';
import Button from '../Button';
import { ALL_ORDERS_FILTER } from '../ClaimPayments/constants';
import { buildOrderFilterOptions, ClaimPaymentOrderFilterOption, formatEmptyPaymentScope } from '../ClaimPayments/order-filter';
import Select from '../FormFields/Select';
import {
  CloseButton,
  DetailLabel,
  DetailTable,
  DetailTableRow,
  DetailValue,
  ModalHeader,
  ModalTitle,
  SECTION_GAP,
  SectionPanel,
  SectionTitle,
  TwoColumnDetailTable,
} from '../DetailsModal';
import { HoverTooltip } from '../HoverTooltip';
import IdentifierValue from '../IdentifierValue';
import InlineTable from '../InlineTable';
import Modal from '../Modal';
import RepositCard from '../Reposit/RepositCard';
import { Table, Tbody, Td, Thead, Tr } from '../Table';
import { Label, P2 } from '../Typography';

// Historical types can be null, and older responses omit the new metadata fields.
type ManualPaymentDetails = Omit<OrderManualPaymentDetailsDTO, 'manualPaymentAdjustment'> & {
  manualPaymentAdjustment?:
    | (Omit<OrderManualPaymentAdjustmentDTO, 'type' | 'externalId'> & {
        type?: ManualPaymentAdjustmentType | null;
        externalId?: string | null;
      })
    | null;
};

interface ManualPaymentsProps {
  payments: ManualPaymentDetails[];
  isLoading: boolean;
  error?: string;
  title?: string;
  activeOrderId?: string;
  orderFilterOptions?: ClaimPaymentOrderFilterOption[];
}

interface ManualPaymentDetailsModalProps {
  payment: ManualPaymentDetails;
  onDismiss: () => void;
}

interface DetailRow {
  label: string;
  value: string;
  monospace?: boolean;
}

const INITIAL_VISIBLE_ROW_COUNT = 5;
const SOURCE_LABELS: Record<OrderManualPaymentSource, string> = {
  [OrderManualPaymentSource.BANKTRANSFER]: 'Bank transfer',
  [OrderManualPaymentSource.CARS]: 'CARs',
  [OrderManualPaymentSource.ADJUSTMENT]: 'Adjustment',
  [OrderManualPaymentSource.MANUALENTRY]: 'Manual entry',
};

const ADJUSTMENT_TYPES: Record<ManualPaymentAdjustmentType, { label: string; description: string }> = {
  [ManualPaymentAdjustmentType.PRODUCTSUPPORTREQUEST]: {
    label: 'Product support request',
    description: 'An adjustment made to resolve a request raised with Product Support.',
  },
  [ManualPaymentAdjustmentType.DATAMIGRATION]: {
    label: 'Data migration',
    description: 'An adjustment created when payment data was imported from another system.',
  },
  [ManualPaymentAdjustmentType.AGENTCOLLECTEDPAYMENT]: {
    label: 'Agent collected payment',
    description: 'The tenant paid the agent directly, outside Reposit.',
  },
  [ManualPaymentAdjustmentType.REPOSITCONTRIBUTION]: {
    label: 'Reposit contribution',
    description:
      'An amount covered by Reposit, for example as goodwill or to correct an adjudication error. The tenant does not owe this amount.',
  },
};

const ActionCell = styled(Td)`
  text-align: right;
`;

const DateCellContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const DateCaption = styled.span`
  color: ${(props) => props.theme.colors.body2};
  font-size: 12px;
  line-height: 1.2;
`;

const SourceCell = styled(Td)`
  padding-bottom: 10px;
  padding-top: 10px;
  white-space: nowrap;
`;

const AdjustmentTypeText = styled.span`
  color: ${(props) => props.theme.colors.body2};
  display: block;
  font-size: 12px;
  line-height: 1.4;
  margin-top: 4px;
  max-width: 180px;
  white-space: normal;
`;

const DescriptionText = styled.span`
  display: block;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ShowMoreButton = styled.button`
  background: transparent;
  border: none;
  color: ${(props) => props.theme.colors.secondaryBrand};
  cursor: pointer;
  display: block;
  font-family: ${(props) => props.theme.typography.face.primary};
  font-weight: bold;
  margin: 18px auto 0;
  outline: none;
`;

const TableScrollContainer = styled.div<{ isExpanded: boolean }>`
  ${(props) =>
    props.isExpanded
      ? `
    max-height: 480px;
    overflow: auto;
  `
      : ''}
`;

const TableFooter = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  margin-top: 18px;
`;

const RowCount = styled.span`
  color: ${(props) => props.theme.colors.body2};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 14px;
  margin-right: 14px;
`;

const SummaryTableContainer = styled.div`
  margin-bottom: 18px;
`;

const CardMessage = styled.div`
  padding: 0 35px 20px;

  ${P2} {
    margin-bottom: 0;
  }
`;

const OrderFilter = styled.div`
  align-items: center;
  display: flex;
  flex: 0 1 300px;
  margin-left: auto;
  max-width: 320px;
  min-width: 150px;
`;

const OrderFilterLabel = styled(Label)`
  flex: 0 0 auto;
  margin: 0 16px 0 0;
`;

const NoMetadataNote = styled(P2)`
  color: ${(props) => props.theme.colors.body2};
  font-style: italic;
  margin-top: 24px;
`;

const MetadataGrid = styled.div`
  display: grid;
  grid-column-gap: ${SECTION_GAP};
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  margin-top: ${SECTION_GAP};
`;

const AdjustmentDetailGrid = styled.div`
  display: grid;
  grid-column-gap: ${SECTION_GAP};
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin: 0;
`;

const AdjustmentField = styled.dl`
  border-bottom: 1px solid ${(props) => props.theme.colors.dividerLight};
  margin: 0;
  min-width: 0;
  padding: 8px 0 10px;
`;

const AdjustmentDescriptionField = styled(AdjustmentField)`
  border-bottom: none;
  grid-column: 1 / -1;
`;

const AdjustmentFieldLabel = styled.dt`
  color: ${(props) => props.theme.colors.body2};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 15px;
  margin-bottom: 8px;
`;

const AdjustmentFieldValue = styled.dd`
  color: ${(props) => props.theme.colors.body};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 15px;
  font-weight: bold;
  line-height: 1.45;
  margin: 0;
  overflow-wrap: anywhere;
`;

const AdjustmentInlineField = styled(AdjustmentField)`
  align-items: center;
  display: flex;
  justify-content: space-between;

  ${AdjustmentFieldLabel} {
    margin-bottom: 0;
    padding-right: 16px;
  }

  ${AdjustmentFieldValue} {
    text-align: right;
  }
`;

const ImportTableGroup = styled.div`
  margin-bottom: ${SECTION_GAP};
`;

const getAdjustmentDetails = (payment: ManualPaymentDetails) => {
  const adjustment = payment.source === OrderManualPaymentSource.ADJUSTMENT ? payment.manualPaymentAdjustment : undefined;
  if (!adjustment) return undefined;

  return {
    ...adjustment,
    typeDetails: adjustment.type ? ADJUSTMENT_TYPES[adjustment.type] : undefined,
    externalId: adjustment.externalId ? adjustment.externalId.trim() : undefined,
  };
};

const getEffectiveDate = (payment: ManualPaymentDetails) => {
  if (payment.source === OrderManualPaymentSource.BANKTRANSFER && payment.bankTransaction) {
    return payment.bankTransaction.transactionDate;
  }

  if (payment.source === OrderManualPaymentSource.CARS && payment.carsPayment) {
    return payment.carsPayment.createdAt;
  }

  return payment.createdAt;
};

const getDateCaption = (payment: ManualPaymentDetails) => {
  return payment.source === OrderManualPaymentSource.BANKTRANSFER && payment.bankTransaction ? 'Date Paid' : 'Date Recorded';
};

const formatDate = (date?: Date | string) => {
  return date ? moment(date).format('DD/MM/YYYY') : '-';
};

const formatDateTime = (date?: Date | string) => {
  return date ? moment(date).format('DD/MM/YYYY HH:mm:ss') : '-';
};

const formatMoney = (value?: number) => {
  return value || value === 0 ? `£${penceToPounds(value)}` : '-';
};

const formatPounds = (value?: number | string) => {
  if (value || value === 0) {
    return `£${Number(value).toFixed(2)}`;
  }

  return '-';
};

const sortPayments = (payments: ManualPaymentDetails[]) => {
  return [...payments].sort((a, b) => moment(getEffectiveDate(b)).valueOf() - moment(getEffectiveDate(a)).valueOf());
};

const DetailValueCell: React.FC<{ row: DetailRow }> = ({ row }) => {
  if (row.monospace && row.value !== '-') {
    return (
      <DetailValue>
        <IdentifierValue as="code" title={row.value}>
          {row.value}
        </IdentifierValue>
      </DetailValue>
    );
  }

  return <DetailValue>{row.value}</DetailValue>;
};

const DetailRows: React.FC<{ rows: DetailRow[] }> = ({ rows }) => (
  <>
    {rows.map((row) => (
      <DetailTableRow key={row.label}>
        <DetailLabel>{row.label}</DetailLabel>
        <DetailValueCell row={row} />
      </DetailTableRow>
    ))}
  </>
);

const ManualPaymentDetailsModal: React.FC<ManualPaymentDetailsModalProps> = ({ payment, onDismiss }) => {
  const bankTransaction = payment.source === OrderManualPaymentSource.BANKTRANSFER ? payment.bankTransaction : undefined;
  const carsPayment = payment.source === OrderManualPaymentSource.CARS ? payment.carsPayment : undefined;
  const manualPaymentAdjustment = getAdjustmentDetails(payment);
  // biome-ignore lint/complexity/useOptionalChain: admin-legacy's production compiler cannot parse optional chaining.
  const bulkImportRow = (bankTransaction && bankTransaction.bulkImportRow) || (carsPayment && carsPayment.bulkImportRow);
  const bulkImport = bulkImportRow && bulkImportRow.bulkImport;
  const hasBulkImportFile = Boolean(bulkImport || (bulkImportRow && bulkImportRow.bulkImportId));
  const hasImportRow = Boolean(bulkImportRow);
  const hasMetadata = Boolean(bankTransaction || carsPayment || manualPaymentAdjustment);
  const manualPaymentRows: DetailRow[] = [
    { label: 'ID', value: payment.id, monospace: true },
    { label: 'Order ID', value: payment.orderId, monospace: true },
    { label: 'Amount', value: formatMoney(payment.amount) },
    { label: 'Created At', value: formatDateTime(payment.createdAt) },
    { label: 'Source', value: SOURCE_LABELS[payment.source] },
  ];

  return (
    <Modal onDismiss={onDismiss} maxWidth="1040px" scrollable capHeight>
      <ModalHeader>
        <ModalTitle>Details</ModalTitle>
        <CloseButton onClick={onDismiss} aria-label="Close payment details">
          &times;
        </CloseButton>
      </ModalHeader>
      <SectionTitle>Manual payment</SectionTitle>
      <TwoColumnDetailTable>
        <DetailRows rows={manualPaymentRows} />
      </TwoColumnDetailTable>
      {!hasMetadata ? (
        <NoMetadataNote>
          This payment was recorded manually and was not linked to a bank transfer, CARs payment, or adjustment.
        </NoMetadataNote>
      ) : null}
      {hasMetadata ? (
        <MetadataGrid>
          {bankTransaction ? (
            <SectionPanel>
              <SectionTitle>Bank transfer</SectionTitle>
              <DetailTable>
                <DetailRows
                  rows={[
                    { label: 'ID', value: bankTransaction.id, monospace: true },
                    { label: 'Date', value: formatDate(bankTransaction.transactionDate) },
                    { label: 'Narrative 1', value: bankTransaction.narrative1 || '-' },
                    { label: 'Narrative 2', value: bankTransaction.narrative2 || '-' },
                    { label: 'Narrative 3', value: bankTransaction.narrative3 || '-' },
                    { label: 'Narrative 4', value: bankTransaction.narrative4 || '-' },
                    { label: 'Narrative 5', value: bankTransaction.narrative5 || '-' },
                    { label: 'Type', value: bankTransaction.type || '-' },
                    { label: 'Debit', value: formatPounds(bankTransaction.debit) },
                    { label: 'Credit', value: formatPounds(bankTransaction.credit) },
                  ]}
                />
              </DetailTable>
            </SectionPanel>
          ) : null}
          {carsPayment ? (
            <SectionPanel>
              <SectionTitle>CARs payment</SectionTitle>
              <DetailTable>
                <DetailRows
                  rows={[
                    { label: 'CARs reference', value: carsPayment.ourReference, monospace: true },
                    { label: 'Debtor name', value: carsPayment.debtorName },
                    { label: 'Gross amount', value: formatPounds(carsPayment.amount) },
                    { label: 'Commission', value: formatPounds(carsPayment.commission) },
                    { label: 'Receipt type', value: carsPayment.receiptType },
                  ]}
                />
              </DetailTable>
            </SectionPanel>
          ) : null}
          {manualPaymentAdjustment ? (
            <SectionPanel>
              <SectionTitle>Adjustment</SectionTitle>
              <AdjustmentDetailGrid role="group" aria-label="Adjustment details">
                <AdjustmentInlineField>
                  <AdjustmentFieldLabel>ID</AdjustmentFieldLabel>
                  <AdjustmentFieldValue>
                    <IdentifierValue title={manualPaymentAdjustment.id}>{manualPaymentAdjustment.id}</IdentifierValue>
                  </AdjustmentFieldValue>
                </AdjustmentInlineField>
                <AdjustmentInlineField>
                  <AdjustmentFieldLabel>Created At</AdjustmentFieldLabel>
                  <AdjustmentFieldValue>{formatDateTime(manualPaymentAdjustment.createdAt)}</AdjustmentFieldValue>
                </AdjustmentInlineField>
                {manualPaymentAdjustment.typeDetails ? (
                  <AdjustmentInlineField>
                    <AdjustmentFieldLabel>Type</AdjustmentFieldLabel>
                    <AdjustmentFieldValue>
                      <HoverTooltip tooltip={manualPaymentAdjustment.typeDetails.description}>
                        {manualPaymentAdjustment.typeDetails.label}
                      </HoverTooltip>
                    </AdjustmentFieldValue>
                  </AdjustmentInlineField>
                ) : null}
                {manualPaymentAdjustment.externalId ? (
                  <AdjustmentInlineField>
                    <AdjustmentFieldLabel>External ID</AdjustmentFieldLabel>
                    <AdjustmentFieldValue>{manualPaymentAdjustment.externalId}</AdjustmentFieldValue>
                  </AdjustmentInlineField>
                ) : null}
                {manualPaymentAdjustment.description ? (
                  <AdjustmentDescriptionField>
                    <AdjustmentFieldLabel>Description</AdjustmentFieldLabel>
                    <AdjustmentFieldValue>{manualPaymentAdjustment.description}</AdjustmentFieldValue>
                  </AdjustmentDescriptionField>
                ) : null}
              </AdjustmentDetailGrid>
            </SectionPanel>
          ) : null}
          {hasBulkImportFile || hasImportRow ? (
            <div>
              {hasBulkImportFile ? (
                <ImportTableGroup>
                  <SectionTitle>Bulk import file</SectionTitle>
                  <DetailTable>
                    <DetailRows
                      rows={[
                        {
                          label: 'ID',
                          value: (bulkImport && bulkImport.id) || (bulkImportRow && bulkImportRow.bulkImportId) || '-',
                          monospace: true,
                        },
                        { label: 'Import name', value: (bulkImport && bulkImport.name) || '-' },
                        { label: 'File name', value: (bulkImport && bulkImport.fileName) || '-' },
                        { label: 'Created At', value: formatDateTime(bulkImport && bulkImport.createdAt) },
                      ]}
                    />
                  </DetailTable>
                </ImportTableGroup>
              ) : null}
              {hasImportRow && bulkImportRow ? (
                <>
                  <SectionTitle>Import row</SectionTitle>
                  <DetailTable>
                    <DetailRows
                      rows={[
                        { label: 'ID', value: bulkImportRow.id, monospace: true },
                        { label: 'Number', value: `${bulkImportRow.rowNumber}` },
                        ...(bulkImportRow.error ? [{ label: 'Error', value: bulkImportRow.error }] : []),
                        { label: 'Created At', value: formatDateTime(bulkImportRow.createdAt) },
                      ]}
                    />
                  </DetailTable>
                </>
              ) : null}
            </div>
          ) : null}
        </MetadataGrid>
      ) : null}
    </Modal>
  );
};

export const ManualPayments: React.FC<ManualPaymentsProps> = ({
  payments,
  isLoading,
  error,
  title = 'Manual payments',
  activeOrderId,
  orderFilterOptions = [],
}) => {
  const [selectedPayment, setSelectedPayment] = useState<ManualPaymentDetails | null>(null);
  const [isShowingAllRows, setIsShowingAllRows] = useState(false);
  const allSortedPayments = useMemo(() => sortPayments(payments), [payments]);
  const defaultOrderId = activeOrderId || (allSortedPayments[0] && allSortedPayments[0].orderId);
  const orderOptions = buildOrderFilterOptions(payments, orderFilterOptions, activeOrderId, defaultOrderId);
  const activeOrderOption = orderOptions.find((option) => option.hasActiveOrder);
  const latestPaymentOrderOption = !activeOrderId ? orderOptions.find((option) => option.hasDefaultOrder) : undefined;
  const defaultOrderOption = activeOrderOption || latestPaymentOrderOption;
  const defaultOrderFilter = defaultOrderOption ? defaultOrderOption.value : ALL_ORDERS_FILTER;
  const [selectedOrderFilter, setSelectedOrderFilter] = useState(defaultOrderFilter);
  const previousDefaultOrderFilter = useRef(defaultOrderFilter);
  const selectedOrderOption = orderOptions.find((option) => option.value === selectedOrderFilter);
  const selectedOrderIds =
    selectedOrderFilter === ALL_ORDERS_FILTER || !selectedOrderOption ? undefined : selectedOrderOption.orderIds;
  const filteredPayments = selectedOrderIds ? payments.filter((payment) => selectedOrderIds.includes(payment.orderId)) : payments;
  const sortedPayments = sortPayments(filteredPayments);
  const visiblePayments = isShowingAllRows ? sortedPayments : sortedPayments.slice(0, INITIAL_VISIBLE_ROW_COUNT);
  const selectedManualPaymentsTotal = sortedPayments.reduce((total, payment) => total + payment.amount, 0);
  const showOrderFilter = orderOptions.length > 0;
  const emptyPaymentScope = selectedOrderFilter === ALL_ORDERS_FILTER ? 'claim' : formatEmptyPaymentScope(selectedOrderOption);
  const emptyMessage = `No manual payments found for ${emptyPaymentScope}.`;

  useEffect(() => {
    if (previousDefaultOrderFilter.current !== defaultOrderFilter) {
      previousDefaultOrderFilter.current = defaultOrderFilter;
      setSelectedOrderFilter(defaultOrderFilter);
    }
  }, [defaultOrderFilter]);

  useEffect(() => {
    const selectedOrderStillExists =
      selectedOrderFilter === ALL_ORDERS_FILTER || orderOptions.some((option) => option.value === selectedOrderFilter);

    if (!selectedOrderStillExists) {
      setSelectedOrderFilter(defaultOrderFilter);
    }
  }, [defaultOrderFilter, orderOptions, selectedOrderFilter]);

  return (
    <RepositCard
      flush
      title={title}
      headerContent={
        showOrderFilter ? (
          <OrderFilter>
            <OrderFilterLabel>Order</OrderFilterLabel>
            <Select
              name="manual-payments-order-filter"
              onChange={(value: string) => {
                setSelectedOrderFilter(value);
                setIsShowingAllRows(false);
              }}
              value={selectedOrderFilter}
            >
              <option value={ALL_ORDERS_FILTER}>All orders</option>
              {orderOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </OrderFilter>
        ) : null
      }
    >
      {selectedPayment ? (
        <ManualPaymentDetailsModal payment={selectedPayment} onDismiss={() => setSelectedPayment(null)} />
      ) : null}
      {isLoading ? <P2>Loading manual payments...</P2> : null}
      {!isLoading && error ? <P2>{error}</P2> : null}
      {!isLoading && !error && sortedPayments.length === 0 ? (
        <CardMessage>
          <P2>{emptyMessage}</P2>
        </CardMessage>
      ) : null}
      {!isLoading && !error && sortedPayments.length > 0 ? (
        <>
          <SummaryTableContainer>
            <InlineTable
              items={[
                { key: 'Total Payments', value: `£${penceToPounds(selectedManualPaymentsTotal)}` },
                { key: 'Number Of Manual Payments', value: `${sortedPayments.length}` },
              ]}
            />
          </SummaryTableContainer>
          <TableScrollContainer isExpanded={isShowingAllRows}>
            <Table>
              <Thead>
                <Tr>
                  <Td title="Bank transaction date or CARs record date if linked, otherwise the manual payment date">Date</Td>
                  <Td title="Manual payment amount">Amount</Td>
                  <Td>Source</Td>
                  <Td>Description</Td>
                  <Td />
                </Tr>
              </Thead>
              <Tbody>
                {visiblePayments.map((payment) => {
                  const adjustment = getAdjustmentDetails(payment);
                  const description = [adjustment && adjustment.externalId, payment.description].filter(Boolean).join(' · ');

                  return (
                    <Tr key={payment.id}>
                      <Td>
                        <DateCellContent>
                          <span>{formatDate(getEffectiveDate(payment))}</span>
                          <DateCaption>{getDateCaption(payment)}</DateCaption>
                        </DateCellContent>
                      </Td>
                      <Td title="Manual payment amount">{formatMoney(payment.amount)}</Td>
                      <SourceCell>
                        {SOURCE_LABELS[payment.source]}
                        {adjustment && adjustment.typeDetails ? (
                          <AdjustmentTypeText>
                            <HoverTooltip tooltip={adjustment.typeDetails.description} portal>
                              {adjustment.typeDetails.label}
                            </HoverTooltip>
                          </AdjustmentTypeText>
                        ) : null}
                      </SourceCell>
                      <Td>
                        <DescriptionText title={description || undefined}>{description || '—'}</DescriptionText>
                      </Td>
                      <ActionCell>
                        <Button noArrow={true} mini="true" onClick={() => setSelectedPayment(payment)} type="button">
                          View details
                        </Button>
                      </ActionCell>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableScrollContainer>
          {sortedPayments.length > INITIAL_VISIBLE_ROW_COUNT ? (
            <TableFooter>
              <RowCount>
                Showing {visiblePayments.length} of {sortedPayments.length} manual payments
              </RowCount>
              <ShowMoreButton type="button" onClick={() => setIsShowingAllRows(!isShowingAllRows)}>
                {isShowingAllRows ? 'Show fewer' : `Show all ${sortedPayments.length}`}
              </ShowMoreButton>
            </TableFooter>
          ) : null}
        </>
      ) : null}
    </RepositCard>
  );
};

export default ManualPayments;
